import { Fragment } from "react";
import { Loader2, Pencil } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { toastCopyableError } from "./copyable-error-toast";
import { buildChatErrorCopyOptions } from "@/lib/format-error-for-copy";
import { parsePhaseDetailsBlocks } from "@/lib/session-structure-format";
import type { Program } from "@/integrations/backend/programs";
import ProgramGenerationChatModal from "./ProgramGenerationChatModal";
import { loadExercisesWithRelations } from "@/integrations/backend/exercises";
import {
  confirmPhaseWorkouts,
  confirmProgramPhases,
  confirmSessionStructure,
  createProgramPhase,
  deleteProgramPhase,
  generatePhaseWorkouts,
  generateProgramPhases,
  generateSessionStructure,
  getPhaseWorkoutsChat,
  getProgramPhasesChat,
  getSessionStructureChat,
  listProgramPhases,
  listSessionStructures,
  parsePhaseWorkoutsAssistantContent,
  parseProgramPhasesAssistantContent,
  parseSessionStructuresAssistantContent,
  restartPhaseWorkouts,
  restartProgramPhases,
  restartSessionStructure,
  sendPhaseWorkoutsMessage,
  sendProgramPhasesMessage,
  sendSessionStructureMessage,
  updateProgramPhase,
  updateSessionStructure,
  type AIGenerationChatMessage,
  type AIGenerationConversation,
  type PhaseWorkoutPreviewPayload,
  type ProgramPhase,
  type SessionStructure,
  type SessionStructuresPreviewPayload,
} from "@/integrations/backend/program-generation";
import { formatISODateLabelLong, isoDatePlusDays } from "@/lib/date-only";
import { labels, motivationOptions } from "@/lib/onboarding-options";
import { parseSessionStructureBlocks } from "@/lib/session-structure-format";

const PHASE_DETAILS_COLUMNS = [
  "Session Block",
  "Exercise / Series",
  "Sets × Reps / Tempo / Rest",
  "Coaching Focus / Notes",
] as const;

type ProgramSubTab = "summary" | "session-structure" | "program-phases";

type PhaseDraftRow = {
  id: string;
  week_start: number;
  week_end: number;
  phase1_start_date: string;
  title: string;
  details: string;
  disabled: boolean;
  session_structure_id: string | null;
  session_structure_name: string;
  isNew?: boolean;
};

interface ClientProgramTabContentProps {
  clientId: string;
  assignedProgram: Program | null;
  onEditProgram: () => void;
  onReassignProgram: () => void;
  onNavigateToWorkouts: () => void;
  onNavigateToWorkoutGenerator: (phaseId: string) => void;
  getDifficultyColor: (level: string) => string;
}

function toPhaseDraftRows(phases: ProgramPhase[]): PhaseDraftRow[] {
  return phases.map((p) => ({
    id: p.id,
    week_start: p.week_start,
    week_end: p.week_end,
    phase1_start_date: p.phase1_start_date ?? "",
    title: p.title,
    details: p.details,
    disabled: p.disabled,
    session_structure_id: p.session_structure_id ?? null,
    session_structure_name: p.session_structure_name ?? "",
  }));
}

function sortSessionStructures(rows: SessionStructure[]): SessionStructure[] {
  return [...rows].sort((a, b) => {
    if (a.disabled !== b.disabled) return a.disabled ? 1 : -1;
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return (b.created_at || "").localeCompare(a.created_at || "");
  });
}

export default function ClientProgramTabContent({
  clientId,
  assignedProgram,
  onEditProgram,
  onReassignProgram,
  onNavigateToWorkouts,
  onNavigateToWorkoutGenerator,
  getDifficultyColor,
}: ClientProgramTabContentProps) {
  const [subTab, setSubTab] = useState<ProgramSubTab>("summary");
  const [loadingData, setLoadingData] = useState(false);

  const [sessionStructures, setSessionStructures] = useState<SessionStructure[]>([]);
  const [programPhases, setProgramPhases] = useState<ProgramPhase[]>([]);

  const [sessionDraft, setSessionDraft] = useState("");
  const [sessionNameDraft, setSessionNameDraft] = useState("");
  const [sessionDisabled, setSessionDisabled] = useState(false);
  const [editingSessionStructureId, setEditingSessionStructureId] = useState<string | null>(null);
  const [savingSession, setSavingSession] = useState(false);
  const [sessionStructureEditorOpen, setSessionStructureEditorOpen] = useState(false);

  const [phaseDraftRows, setPhaseDraftRows] = useState<PhaseDraftRow[]>([]);
  const [removedPhaseIds, setRemovedPhaseIds] = useState<string[]>([]);
  const [savingPhases, setSavingPhases] = useState(false);
  const [phaseEditMode, setPhaseEditMode] = useState(false);
  const [editingProgramStartFor, setEditingProgramStartFor] = useState<string | null>(null);
  const [programStartEditValue, setProgramStartEditValue] = useState("");

  const [sessionChatOpen, setSessionChatOpen] = useState(false);
  const [sessionConversation, setSessionConversation] = useState<AIGenerationConversation | null>(null);
  const [sessionChatInput, setSessionChatInput] = useState("");
  const [sessionChatBusy, setSessionChatBusy] = useState(false);

  const [phasesChatOpen, setPhasesChatOpen] = useState(false);
  const [phasesConversation, setPhasesConversation] = useState<AIGenerationConversation | null>(null);
  const [phasesChatInput, setPhasesChatInput] = useState("");
  const [phasesChatBusy, setPhasesChatBusy] = useState(false);
  const [workoutsChatOpen, setWorkoutsChatOpen] = useState(false);
  const [workoutsConversation, setWorkoutsConversation] = useState<AIGenerationConversation | null>(null);
  const [workoutsChatInput, setWorkoutsChatInput] = useState("");
  const [workoutsChatBusy, setWorkoutsChatBusy] = useState(false);
  const [selectedPhaseForWorkouts, setSelectedPhaseForWorkouts] = useState<ProgramPhase | null>(null);
  const [exerciseNameMap, setExerciseNameMap] = useState<Record<string, string>>({});

  const sortedSessionStructures = useMemo(
    () => sortSessionStructures(sessionStructures),
    [sessionStructures]
  );
  const activeSessionStructures = useMemo(
    () => sortedSessionStructures.filter((s) => !s.disabled),
    [sortedSessionStructures]
  );
  const hasSessionStructure = activeSessionStructures.length > 0 || sessionStructures.length > 0;
  const hasProgramPhases = programPhases.length > 0;
  const programFrequency = assignedProgram?.frequency ?? 0;
  const sessionStructureCountMismatch =
    programFrequency > 0 &&
    activeSessionStructures.length > 0 &&
    activeSessionStructures.length > programFrequency;

  const refreshSavedData = async () => {
    if (!clientId) return;
    setLoadingData(true);
    try {
      const [sessionRows, phaseRows] = await Promise.all([
        listSessionStructures(clientId),
        listProgramPhases(clientId),
      ]);
      const sorted = sortSessionStructures(sessionRows);
      setSessionStructures(sorted);
      setProgramPhases(phaseRows);
      setPhaseDraftRows(toPhaseDraftRows(phaseRows));
      setRemovedPhaseIds([]);
    } catch (error) {
      console.error("Failed to load program generation data", error);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    void refreshSavedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (subTab === "session-structure" && !hasSessionStructure) {
      setSubTab("summary");
    }
    if (subTab === "program-phases" && !hasProgramPhases) {
      setSubTab("summary");
    }
  }, [subTab, hasSessionStructure, hasProgramPhases]);

  useEffect(() => {
    if (!workoutsChatOpen) return;
    void loadExercisesWithRelations().then((exercises) => {
      const map: Record<string, string> = {};
      for (const ex of exercises) {
        map[ex.id] = ex.name;
      }
      setExerciseNameMap(map);
    });
  }, [workoutsChatOpen]);

  const sessionAssistantMessages = useMemo(
    () => (sessionConversation?.messages ?? []).filter((m) => m.role === "assistant"),
    [sessionConversation]
  );
  const latestSessionStructuresPreview = useMemo<SessionStructuresPreviewPayload | null>(() => {
    if (
      sessionConversation?.parsed_preview &&
      "session_structures" in sessionConversation.parsed_preview
    ) {
      return sessionConversation.parsed_preview;
    }
    const lastAssistant = sessionAssistantMessages[sessionAssistantMessages.length - 1];
    return parseSessionStructuresAssistantContent(lastAssistant?.content ?? "");
  }, [sessionConversation, sessionAssistantMessages]);
  const phasesAssistantMessages = useMemo(
    () => (phasesConversation?.messages ?? []).filter((m) => m.role === "assistant"),
    [phasesConversation]
  );
  const latestPhasePreview = useMemo(() => {
    if (phasesConversation?.parsed_preview && "phases" in phasesConversation.parsed_preview) {
      return phasesConversation.parsed_preview;
    }
    const lastAssistant = phasesAssistantMessages[phasesAssistantMessages.length - 1];
    return parseProgramPhasesAssistantContent(lastAssistant?.content ?? "");
  }, [phasesConversation, phasesAssistantMessages]);
  const workoutsAssistantMessages = useMemo(
    () => (workoutsConversation?.messages ?? []).filter((m) => m.role === "assistant"),
    [workoutsConversation]
  );
  const latestWorkoutPreview = useMemo<PhaseWorkoutPreviewPayload | null>(() => {
    if (workoutsConversation?.parsed_preview && "workouts" in workoutsConversation.parsed_preview) {
      return workoutsConversation.parsed_preview;
    }
    const lastAssistant = workoutsAssistantMessages[workoutsAssistantMessages.length - 1];
    return parsePhaseWorkoutsAssistantContent(lastAssistant?.content ?? "");
  }, [workoutsConversation, workoutsAssistantMessages]);

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const assistantMessageCount = (conversation: AIGenerationConversation | null): number =>
    (conversation?.messages ?? []).filter((m) => m.role === "assistant").length;

  const waitForAssistantResponse = async ({
    conversationId,
    initialAssistantCount,
    fetchConversation,
    applyConversation,
  }: {
    conversationId: string | null;
    initialAssistantCount: number;
    fetchConversation: (conversationId: string) => Promise<AIGenerationConversation>;
    applyConversation: (conversation: AIGenerationConversation) => void;
  }): Promise<AIGenerationConversation> => {
    if (!conversationId) {
      throw new Error("conversation_id is required");
    }
    const timeoutMs = 600_000;
    const intervalMs = 2_000;
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      await sleep(intervalMs);
      const next = await fetchConversation(conversationId);
      applyConversation(next);
      if (assistantMessageCount(next) > initialAssistantCount) {
        return next;
      }
    }
    throw new Error("Timed out waiting for AI response");
  };

  const handleGenerateSessionStructure = async () => {
    if (!assignedProgram) return;
    setSessionChatBusy(true);
    let errorConversation = sessionConversation;
    const applySessionConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setSessionConversation(next);
    };
    try {
      const conversation = await generateSessionStructure(clientId);
      applySessionConversation(conversation);
      setSessionChatOpen(true);
      setSessionChatInput("");
      await waitForAssistantResponse({
        conversationId: conversation.conversation_id,
        initialAssistantCount: assistantMessageCount(conversation),
        fetchConversation: (conversationId) => getSessionStructureChat(clientId, conversationId),
        applyConversation: applySessionConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to generate session structure", buildChatErrorCopyOptions(error, errorConversation));
    } finally {
      setSessionChatBusy(false);
    }
  };

  const handleSendSessionMessage = async () => {
    const content = sessionChatInput.trim();
    const conversationId = sessionConversation?.conversation_id;
    if (!content || !conversationId) return;
    const optimisticMessage: AIGenerationChatMessage = {
      id: `optimistic-${Date.now()}`,
      profile_id: clientId,
      chat_type: "session_structure",
      conversation_id: conversationId,
      role: "user",
      content,
      created_at: null,
      updated_at: null,
    };
    setSessionConversation((prev) =>
      prev
        ? { ...prev, messages: [...prev.messages, optimisticMessage] }
        : prev
    );
    setSessionChatInput("");
    setSessionChatBusy(true);
    let errorConversation = sessionConversation;
    const applySessionConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setSessionConversation(next);
    };
    try {
      const next = await sendSessionStructureMessage(clientId, {
        conversation_id: conversationId,
        content,
      });
      applySessionConversation(next);
      await waitForAssistantResponse({
        conversationId: next.conversation_id,
        initialAssistantCount: assistantMessageCount(next),
        fetchConversation: (nextConversationId) => getSessionStructureChat(clientId, nextConversationId),
        applyConversation: applySessionConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(
        error,
        "Failed to send message",
        buildChatErrorCopyOptions(error, errorConversation, content)
      );
      setSessionConversation((prev) =>
        prev
          ? { ...prev, messages: prev.messages.filter((m) => m.id !== optimisticMessage.id) }
          : prev
      );
    } finally {
      setSessionChatBusy(false);
    }
  };

  const handleRestartSessionConversation = async () => {
    setSessionChatBusy(true);
    let errorConversation = sessionConversation;
    const applySessionConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setSessionConversation(next);
    };
    try {
      const next = await restartSessionStructure(clientId);
      applySessionConversation(next);
      setSessionChatInput("");
      await waitForAssistantResponse({
        conversationId: next.conversation_id,
        initialAssistantCount: assistantMessageCount(next),
        fetchConversation: (conversationId) => getSessionStructureChat(clientId, conversationId),
        applyConversation: applySessionConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to restart conversation", buildChatErrorCopyOptions(error, errorConversation));
    } finally {
      setSessionChatBusy(false);
    }
  };

  const handleConfirmSessionStructure = async () => {
    const conversationId = sessionConversation?.conversation_id;
    if (!conversationId) return;
    setSessionChatBusy(true);
    try {
      await confirmSessionStructure(clientId, conversationId);
      await refreshSavedData();
      setSessionChatOpen(false);
      setSubTab("session-structure");
      toast.success("Session structures saved");
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to confirm and save", buildChatErrorCopyOptions(error, sessionConversation));
    } finally {
      setSessionChatBusy(false);
    }
  };

  const handleGenerateProgramPhases = async () => {
    if (!assignedProgram) return;
    if (activeSessionStructures.length === 0) {
      toast.error("Confirm at least one session structure before generating phases");
      return;
    }
    if (sessionStructureCountMismatch) {
      toast.message(
        `Active session structures (${activeSessionStructures.length}) exceed program frequency max (${programFrequency}/week). Continuing anyway.`
      );
    }
    setPhasesChatBusy(true);
    let errorConversation = phasesConversation;
    const applyPhasesConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setPhasesConversation(next);
    };
    try {
      const conversation = await generateProgramPhases(clientId);
      applyPhasesConversation(conversation);
      setPhasesChatOpen(true);
      setPhasesChatInput("");
      await waitForAssistantResponse({
        conversationId: conversation.conversation_id,
        initialAssistantCount: assistantMessageCount(conversation),
        fetchConversation: (conversationId) => getProgramPhasesChat(clientId, conversationId),
        applyConversation: applyPhasesConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to generate program phases", buildChatErrorCopyOptions(error, errorConversation));
    } finally {
      setPhasesChatBusy(false);
    }
  };

  const handleSendPhasesMessage = async () => {
    const content = phasesChatInput.trim();
    const conversationId = phasesConversation?.conversation_id;
    if (!content || !conversationId) return;
    const optimisticMessage: AIGenerationChatMessage = {
      id: `optimistic-${Date.now()}`,
      profile_id: clientId,
      chat_type: "program_phases",
      conversation_id: conversationId,
      role: "user",
      content,
      created_at: null,
      updated_at: null,
    };
    setPhasesConversation((prev) =>
      prev
        ? { ...prev, messages: [...prev.messages, optimisticMessage] }
        : prev
    );
    setPhasesChatInput("");
    setPhasesChatBusy(true);
    let errorConversation = phasesConversation;
    const applyPhasesConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setPhasesConversation(next);
    };
    try {
      const next = await sendProgramPhasesMessage(clientId, {
        conversation_id: conversationId,
        content,
      });
      applyPhasesConversation(next);
      await waitForAssistantResponse({
        conversationId: next.conversation_id,
        initialAssistantCount: assistantMessageCount(next),
        fetchConversation: (nextConversationId) => getProgramPhasesChat(clientId, nextConversationId),
        applyConversation: applyPhasesConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(
        error,
        "Failed to send message",
        buildChatErrorCopyOptions(error, errorConversation, content)
      );
      setPhasesConversation((prev) =>
        prev
          ? { ...prev, messages: prev.messages.filter((m) => m.id !== optimisticMessage.id) }
          : prev
      );
    } finally {
      setPhasesChatBusy(false);
    }
  };

  const handleRestartPhasesConversation = async () => {
    setPhasesChatBusy(true);
    let errorConversation = phasesConversation;
    const applyPhasesConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setPhasesConversation(next);
    };
    try {
      const next = await restartProgramPhases(clientId);
      applyPhasesConversation(next);
      setPhasesChatInput("");
      await waitForAssistantResponse({
        conversationId: next.conversation_id,
        initialAssistantCount: assistantMessageCount(next),
        fetchConversation: (conversationId) => getProgramPhasesChat(clientId, conversationId),
        applyConversation: applyPhasesConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to restart conversation", buildChatErrorCopyOptions(error, errorConversation));
    } finally {
      setPhasesChatBusy(false);
    }
  };

  const handleConfirmPhases = async () => {
    const conversationId = phasesConversation?.conversation_id;
    if (!conversationId) return;
    setPhasesChatBusy(true);
    try {
      await confirmProgramPhases(clientId, conversationId);
      await refreshSavedData();
      setPhasesChatOpen(false);
      setSubTab("program-phases");
      toast.success("Program phases saved");
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to confirm and save phases", buildChatErrorCopyOptions(error, phasesConversation));
    } finally {
      setPhasesChatBusy(false);
    }
  };

  /** @deprecated Use onNavigateToWorkoutGenerator / WorkoutGeneratorPage instead. */
  const handleGenerateWorkoutsForPhase = async (phase: ProgramPhase) => {
    setSelectedPhaseForWorkouts(phase);
    setWorkoutsChatBusy(true);
    let errorConversation = workoutsConversation;
    const applyWorkoutsConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setWorkoutsConversation(next);
    };
    try {
      const conversation = await generatePhaseWorkouts(clientId, phase.id);
      applyWorkoutsConversation(conversation);
      setWorkoutsChatInput("");
      setWorkoutsChatOpen(true);
      await waitForAssistantResponse({
        conversationId: conversation.conversation_id,
        initialAssistantCount: assistantMessageCount(conversation),
        fetchConversation: (conversationId) => getPhaseWorkoutsChat(clientId, phase.id, conversationId),
        applyConversation: applyWorkoutsConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to generate workouts", buildChatErrorCopyOptions(error, errorConversation));
    } finally {
      setWorkoutsChatBusy(false);
    }
  };

  const handleSendWorkoutsMessage = async () => {
    const phaseId = selectedPhaseForWorkouts?.id;
    const content = workoutsChatInput.trim();
    const conversationId = workoutsConversation?.conversation_id;
    if (!phaseId || !content || !conversationId) return;
    const optimisticMessage: AIGenerationChatMessage = {
      id: `optimistic-${Date.now()}`,
      profile_id: clientId,
      chat_type: "phase_workouts",
      conversation_id: conversationId,
      role: "user",
      content,
      created_at: null,
      updated_at: null,
    };
    setWorkoutsConversation((prev) =>
      prev ? { ...prev, messages: [...prev.messages, optimisticMessage] } : prev
    );
    setWorkoutsChatInput("");
    setWorkoutsChatBusy(true);
    let errorConversation = workoutsConversation;
    const applyWorkoutsConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setWorkoutsConversation(next);
    };
    try {
      const next = await sendPhaseWorkoutsMessage(clientId, phaseId, {
        conversation_id: conversationId,
        content,
      });
      applyWorkoutsConversation(next);
      await waitForAssistantResponse({
        conversationId: next.conversation_id,
        initialAssistantCount: assistantMessageCount(next),
        fetchConversation: (nextConversationId) => getPhaseWorkoutsChat(clientId, phaseId, nextConversationId),
        applyConversation: applyWorkoutsConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(
        error,
        "Failed to send message",
        buildChatErrorCopyOptions(error, errorConversation, content)
      );
      setWorkoutsConversation((prev) =>
        prev
          ? { ...prev, messages: prev.messages.filter((m) => m.id !== optimisticMessage.id) }
          : prev
      );
    } finally {
      setWorkoutsChatBusy(false);
    }
  };

  const handleRestartWorkoutsConversation = async () => {
    const phaseId = selectedPhaseForWorkouts?.id;
    if (!phaseId) return;
    setWorkoutsChatBusy(true);
    let errorConversation = workoutsConversation;
    const applyWorkoutsConversation = (next: AIGenerationConversation) => {
      errorConversation = next;
      setWorkoutsConversation(next);
    };
    try {
      const next = await restartPhaseWorkouts(clientId, phaseId);
      applyWorkoutsConversation(next);
      setWorkoutsChatInput("");
      await waitForAssistantResponse({
        conversationId: next.conversation_id,
        initialAssistantCount: assistantMessageCount(next),
        fetchConversation: (conversationId) => getPhaseWorkoutsChat(clientId, phaseId, conversationId),
        applyConversation: applyWorkoutsConversation,
      });
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to restart conversation", buildChatErrorCopyOptions(error, errorConversation));
    } finally {
      setWorkoutsChatBusy(false);
    }
  };

  const handleConfirmWorkouts = async () => {
    const phaseId = selectedPhaseForWorkouts?.id;
    const conversationId = workoutsConversation?.conversation_id;
    if (!phaseId || !conversationId) return;
    setWorkoutsChatBusy(true);
    try {
      await confirmPhaseWorkouts(clientId, phaseId, conversationId);
      setWorkoutsChatOpen(false);
      onNavigateToWorkouts();
      toast.success("Workouts created");
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to save workouts", buildChatErrorCopyOptions(error, workoutsConversation));
    } finally {
      setWorkoutsChatBusy(false);
    }
  };

  const openSessionStructureEditor = (row: SessionStructure) => {
    setEditingSessionStructureId(row.id);
    setSessionNameDraft(row.name || "");
    setSessionDraft(row.structure ?? "");
    setSessionDisabled(!!row.disabled);
    setSessionStructureEditorOpen(true);
  };

  const saveSessionStructure = async (): Promise<boolean> => {
    if (!editingSessionStructureId) return false;
    setSavingSession(true);
    try {
      const saved = await updateSessionStructure(clientId, editingSessionStructureId, {
        name: sessionNameDraft.trim(),
        structure: sessionDraft,
        disabled: sessionDisabled,
      });
      setSessionStructures((prev) =>
        sortSessionStructures([saved, ...prev.filter((p) => p.id !== saved.id)])
      );
      toast.success("Session structure updated");
      return true;
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to update session structure");
      return false;
    } finally {
      setSavingSession(false);
    }
  };

  const addPhaseRow = () => {
    const defaultDate =
      phasesGroupedByStartDate.length === 1 && phasesGroupedByStartDate[0].date
        ? phasesGroupedByStartDate[0].date
        : "";
    setPhaseDraftRows((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        week_start: 1,
        week_end: 1,
        phase1_start_date: defaultDate,
        title: "",
        details: "",
        disabled: false,
        session_structure_id: null,
        session_structure_name: "",
        isNew: true,
      },
    ]);
  };

  const removePhaseRow = (id: string) => {
    setPhaseDraftRows((prev) => prev.filter((row) => row.id !== id));
    if (!id.startsWith("new-")) {
      setRemovedPhaseIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
  };

  const updatePhaseRow = (id: string, patch: Partial<PhaseDraftRow>) => {
    setPhaseDraftRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  };

  const savePhases = async () => {
    setSavingPhases(true);
    try {
      for (const removeId of removedPhaseIds) {
        await deleteProgramPhase(clientId, removeId);
      }
      for (const row of phaseDraftRows) {
        const payload = {
          week_start: Number(row.week_start),
          week_end: Number(row.week_end),
          phase1_start_date: row.phase1_start_date,
          title: row.title.trim(),
          details: row.details.trim(),
          disabled: !!row.disabled,
        };
        if (!payload.title || !payload.details || !payload.phase1_start_date) {
          throw new Error("Each phase requires title, details, and phase1 start date");
        }
        if (payload.week_start <= 0 || payload.week_end < payload.week_start) {
          throw new Error("Each phase must have a valid week range");
        }
        if (row.id.startsWith("new-")) {
          await createProgramPhase(clientId, payload);
        } else {
          await updateProgramPhase(clientId, row.id, payload);
        }
      }
      await refreshSavedData();
      toast.success("Program phases saved");
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to save phases");
    } finally {
      setSavingPhases(false);
    }
  };

  const saveProgramStartForGroup = async (currentDate: string, newDate: string) => {
    const existingIds = phaseDraftRows
      .filter((r) => r.phase1_start_date === currentDate && !r.id.startsWith("new-"))
      .map((r) => r.id);
    setSavingPhases(true);
    try {
      for (const id of existingIds) {
        await updateProgramPhase(clientId, id, { phase1_start_date: newDate });
      }
      setPhaseDraftRows((prev) =>
        prev.map((r) =>
          r.phase1_start_date === currentDate ? { ...r, phase1_start_date: newDate } : r
        )
      );
      if (existingIds.length > 0 && !phaseDraftRows.some((r) => r.id.startsWith("new-"))) {
        await refreshSavedData();
      }
      setEditingProgramStartFor(null);
      toast.success("Program start updated");
    } catch (error: unknown) {
      toastCopyableError(error, "Failed to update program start");
    } finally {
      setSavingPhases(false);
    }
  };

  const phasesGroupedByStartDate = useMemo(() => {
    const groups = new Map<string, PhaseDraftRow[]>();
    for (const row of phaseDraftRows) {
      const key = row.phase1_start_date || "";
      const list = groups.get(key) ?? [];
      list.push(row);
      groups.set(key, list);
    }
    return Array.from(groups.entries()).map(([date, rows]) => ({ date, rows }));
  }, [phaseDraftRows]);

  /** Nested outer-phase groups: program start → week range → sibling session-structure phases. */
  const phasesGroupedByOuterPhase = useMemo(() => {
    return phasesGroupedByStartDate.map(({ date, rows }) => {
      const weekMap = new Map<string, PhaseDraftRow[]>();
      for (const row of rows) {
        const weekKey = `${row.week_start}|${row.week_end}`;
        const list = weekMap.get(weekKey) ?? [];
        list.push(row);
        weekMap.set(weekKey, list);
      }
      const weekGroups = Array.from(weekMap.entries())
        .map(([weekKey, weekRows]) => {
          const [ws, we] = weekKey.split("|").map((n) => Number(n));
          const sorted = [...weekRows].sort((a, b) => {
            const nameA = (a.session_structure_name || a.title || "").toLowerCase();
            const nameB = (b.session_structure_name || b.title || "").toLowerCase();
            if (nameA !== nameB) return nameA.localeCompare(nameB);
            return a.title.localeCompare(b.title);
          });
          const anchor =
            sorted.find((r) => !r.id.startsWith("new-") && !r.disabled) ??
            sorted.find((r) => !r.id.startsWith("new-")) ??
            sorted[0];
          return {
            week_start: ws,
            week_end: we,
            rows: sorted,
            anchorId: anchor?.id ?? null,
            hasUnsaved: sorted.some((r) => r.id.startsWith("new-")),
          };
        })
        .sort((a, b) => a.week_start - b.week_start || a.week_end - b.week_end);
      return { date, weekGroups };
    });
  }, [phasesGroupedByStartDate]);

  const anyAiBusy = sessionChatBusy || phasesChatBusy || workoutsChatBusy;

  return (
    <Tabs value={subTab} onValueChange={(v) => setSubTab(v as ProgramSubTab)} className="space-y-4">
      {anyAiBusy && (
        <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 text-sm text-gray-700">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
          AI is generating... Please wait.
        </div>
      )}
      <TabsList className="bg-gray-100">
        <TabsTrigger value="summary">Assigned Program Summary</TabsTrigger>
        {hasSessionStructure && <TabsTrigger value="session-structure">Session Structures</TabsTrigger>}
        {hasProgramPhases && <TabsTrigger value="program-phases">Program Phases</TabsTrigger>}
      </TabsList>

      <TabsContent value="summary" className="space-y-4">
        {assignedProgram ? (
          <Card className="border-0 shadow-lg bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Assigned Program</CardTitle>
                <p className="text-sm text-gray-700">High-level details of the current program</p>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={handleGenerateSessionStructure} disabled={sessionChatBusy}>
                  {sessionChatBusy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    "Generate Session Structures"
                  )}
                </Button>
                <Button variant="outline" onClick={onEditProgram}>
                  Edit Program
                </Button>
                <Button className="bg-gradient-to-r from-primary to-lime-hover" onClick={onReassignProgram}>
                  Reassign Program
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg">
                    <div className="text-xs text-gray-700 mb-1">Program Name</div>
                    <div className="font-semibold text-gray-900">{assignedProgram.name}</div>
                    <div className="mt-2">
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                        Active
                      </Badge>
                    </div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-xs text-gray-700 mb-1">Duration</div>
                    <div className="font-semibold text-gray-800">
                      {assignedProgram.duration} weeks • {assignedProgram.frequency}x per week
                    </div>
                    {assignedProgram.weekly_schedule_preference?.trim() ? (
                      <div className="mt-2 text-xs text-gray-600">
                        Preferred scheduling: {assignedProgram.weekly_schedule_preference}
                      </div>
                    ) : null}
                  </div>
                  <div className="p-4 border rounded-lg">
                    <div className="text-xs text-gray-700 mb-1">Difficulty</div>
                    <div className="mt-1">
                      <Badge className={getDifficultyColor(assignedProgram.difficulty)}>
                        {assignedProgram.difficulty}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="text-sm font-semibold mb-2 text-gray-900">Description</div>
                  <p className="text-sm text-gray-700">{assignedProgram.description}</p>
                </div>

                {assignedProgram.motivations.length > 0 && (
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm font-semibold mb-2 text-gray-900">Motivations</div>
                    <div className="flex flex-wrap gap-2">
                      {assignedProgram.motivations.map((id) => {
                        const motivation = motivationOptions.find((m) => m.id === id);
                        return motivation ? (
                          <Badge key={id} variant="secondary" className="text-xs">
                            {motivation.icon} {motivation.label}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {assignedProgram.exercise_types.length > 0 && (
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm font-semibold mb-2 text-gray-900">Exercise Types</div>
                    <div className="flex flex-wrap gap-2">
                      {assignedProgram.exercise_types.map((id) => {
                        const label = labels.exerciseType[id] ?? id;
                        return (
                          <Badge key={id} variant="outline" className="text-xs">
                            {label}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}

                {assignedProgram.exercise_type_details &&
                  Object.keys(assignedProgram.exercise_type_details).length > 0 && (
                    <div className="p-4 border rounded-lg">
                      <div className="text-sm font-semibold mb-2 text-gray-900">Exercise Type Details</div>
                      <div className="space-y-2">
                        {Object.entries(assignedProgram.exercise_type_details).map(([id, detail]) => {
                          const label = labels.exerciseType[id] ?? id;
                          if (!detail?.trim()) return null;
                          return (
                            <div key={id} className="rounded-md border bg-gray-50 p-3">
                              <div className="text-xs font-semibold text-gray-900">{label}</div>
                              <div className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{detail}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {assignedProgram.program_focus && (
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm font-semibold mb-2 text-gray-900">Program Focus</div>
                    <p className="text-sm text-gray-700">{assignedProgram.program_focus}</p>
                  </div>
                )}

                {assignedProgram.what_to_expect && (
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm font-semibold mb-2 text-gray-900">What to Expect</div>
                    <p className="text-sm text-gray-700">{assignedProgram.what_to_expect}</p>
                  </div>
                )}

                {assignedProgram.variety && (
                  <div className="p-4 border rounded-lg">
                    <div className="text-sm font-semibold mb-2 text-gray-900">Exercise Variety</div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{assignedProgram.variety}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-lg bg-white">
            <CardContent className="py-10 text-center text-sm text-gray-600 space-y-4">
              <div>No assigned program.</div>
              <Button className="bg-gradient-to-r from-primary to-lime-hover" onClick={onReassignProgram}>
                Assign Program
              </Button>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {hasSessionStructure && (
        <TabsContent value="session-structure" className="space-y-4">
          <Card className="border-0 shadow-lg bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Session Structures</CardTitle>
                <p className="text-sm text-gray-600">
                  {activeSessionStructures.length} active
                  {programFrequency > 0 ? ` · program frequency up to ${programFrequency}/week` : ""}
                </p>
                {sessionStructureCountMismatch ? (
                  <p className="mt-1 text-xs text-amber-700">
                    Active count exceeds program frequency max. Regenerate or edit before
                    generating phases for best results.
                  </p>
                ) : null}
              </div>
              <Button variant="outline" onClick={handleGenerateProgramPhases} disabled={phasesChatBusy}>
                {phasesChatBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  "Generate Program Phases"
                )}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingData ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                </div>
              ) : (
                <div className="space-y-4">
                  {sortedSessionStructures.map((row) => {
                    const blocks = parseSessionStructureBlocks(row.structure || "");
                    return (
                      <div
                        key={row.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openSessionStructureEditor(row)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openSessionStructureEditor(row);
                          }
                        }}
                        className={`cursor-pointer rounded-lg border p-4 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                          row.disabled
                            ? "border-gray-200 bg-gray-50/80 opacity-70"
                            : "border-gray-200 bg-gray-50/50"
                        }`}
                        aria-label={`Edit session structure ${row.name || row.sort_order}`}
                      >
                        <div className="mb-3 flex flex-wrap items-center gap-2">
                          <div className="font-semibold text-gray-900">
                            {row.name?.trim() || `Session ${row.sort_order}`}
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            #{row.sort_order}
                          </Badge>
                          {row.disabled ? (
                            <Badge variant="outline" className="text-xs text-gray-600">
                              Disabled
                            </Badge>
                          ) : null}
                        </div>
                        {!(row.structure || "").trim() ? (
                          <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
                            <Pencil className="h-4 w-4 shrink-0" />
                            Click to add session structure
                          </div>
                        ) : (
                          <div className="space-y-3 text-sm">
                            {blocks.map((block, idx) =>
                              block.type === "text" ? (
                                <div key={idx} className="whitespace-pre-wrap text-gray-700">
                                  {block.content}
                                </div>
                              ) : (
                                <div
                                  key={idx}
                                  className="overflow-x-auto rounded-lg border border-gray-200 bg-white"
                                >
                                  <table className="w-full min-w-[280px] text-left text-sm">
                                    <thead>
                                      <tr className="border-b border-gray-200 bg-gray-50">
                                        <th className="px-3 py-2 font-semibold text-gray-700 w-8">#</th>
                                        <th className="px-3 py-2 font-semibold text-gray-700">
                                          Session Flow
                                        </th>
                                        <th className="px-3 py-2 font-semibold text-gray-700">
                                          Purpose
                                        </th>
                                        <th className="px-3 py-2 font-semibold text-gray-700">
                                          Duration
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="text-gray-900">
                                      {block.rows.map((tableRow, rIdx) => (
                                        <tr
                                          key={rIdx}
                                          className="border-b border-gray-100 last:border-0"
                                        >
                                          <td className="px-3 py-2 text-gray-500">{tableRow.num}.</td>
                                          <td className="px-3 py-2 font-medium">{tableRow.col1}</td>
                                          <td className="px-3 py-2">{tableRow.col2}</td>
                                          <td className="px-3 py-2 text-gray-600">{tableRow.col3}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )
                            )}
                            <div className="flex items-center gap-1.5 pt-1 text-xs text-gray-500">
                              <Pencil className="h-3.5 w-3.5" />
                              Click to edit
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog
            open={sessionStructureEditorOpen}
            onOpenChange={(open) => {
              setSessionStructureEditorOpen(open);
              if (!open) setEditingSessionStructureId(null);
            }}
          >
            <DialogScrollContent className="max-h-[90vh] max-w-2xl border border-gray-200 bg-white text-gray-900">
              <DialogHeader className="shrink-0">
                <DialogTitle>Edit Session Structure</DialogTitle>
              </DialogHeader>
              <DialogBody className="space-y-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={sessionNameDraft}
                    onChange={(e) => setSessionNameDraft(e.target.value)}
                    placeholder="e.g. Push"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Structure (plain text)</Label>
                  <Textarea
                    value={sessionDraft}
                    onChange={(e) => setSessionDraft(e.target.value)}
                    rows={14}
                    placeholder={"1. Session Flow --- Purpose --- Duration\n2. ..."}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="session-structure-editor-disabled"
                    checked={sessionDisabled}
                    onCheckedChange={(checked) => setSessionDisabled(!!checked)}
                  />
                  <Label htmlFor="session-structure-editor-disabled">Disabled</Label>
                </div>
              </DialogBody>
              <DialogFooter className="shrink-0">
                <Button
                  onClick={async () => {
                    const ok = await saveSessionStructure();
                    if (ok) setSessionStructureEditorOpen(false);
                  }}
                  disabled={savingSession || !editingSessionStructureId}
                >
                  {savingSession ? "Saving..." : "Save Session Structure"}
                </Button>
              </DialogFooter>
            </DialogScrollContent>
          </Dialog>
        </TabsContent>
      )}

      <ProgramGenerationChatModal
        open={sessionChatOpen}
        onOpenChange={setSessionChatOpen}
        title="Session Structures"
        formatAsSessionStructure
        conversation={sessionConversation}
        input={sessionChatInput}
        onInputChange={setSessionChatInput}
        busy={sessionChatBusy}
        onSend={handleSendSessionMessage}
        onRestart={handleRestartSessionConversation}
        onConfirm={handleConfirmSessionStructure}
        canConfirm={sessionAssistantMessages.length > 0}
        sessionStructuresPreview={latestSessionStructuresPreview}
        parseError={sessionConversation?.parse_error}
      />

      <ProgramGenerationChatModal
        open={phasesChatOpen}
        onOpenChange={setPhasesChatOpen}
        title="Program Phases"
        conversation={phasesConversation}
        input={phasesChatInput}
        onInputChange={setPhasesChatInput}
        busy={phasesChatBusy}
        onSend={handleSendPhasesMessage}
        onRestart={handleRestartPhasesConversation}
        onConfirm={handleConfirmPhases}
        canConfirm={phasesAssistantMessages.length > 0}
        phasePreview={latestPhasePreview}
        parseError={phasesConversation?.parse_error}
      />

      <ProgramGenerationChatModal
        open={workoutsChatOpen}
        onOpenChange={setWorkoutsChatOpen}
        title={
          selectedPhaseForWorkouts
            ? `Generate Workouts - ${selectedPhaseForWorkouts.title}`
            : "Generate Workouts"
        }
        conversation={workoutsConversation}
        input={workoutsChatInput}
        onInputChange={setWorkoutsChatInput}
        busy={workoutsChatBusy}
        onSend={handleSendWorkoutsMessage}
        onRestart={handleRestartWorkoutsConversation}
        onConfirm={handleConfirmWorkouts}
        canConfirm={workoutsAssistantMessages.length > 0}
        confirmLabel="Save Workouts"
        workoutPreview={latestWorkoutPreview}
        exerciseNameMap={exerciseNameMap}
        parseError={workoutsConversation?.parse_error}
      />

      {hasProgramPhases && (
        <TabsContent value="program-phases" className="space-y-4">
          <Card className="border-0 shadow-lg bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Program Phases</CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPhaseEditMode((e) => !e)}
                >
                  {phaseEditMode ? "Done" : "Edit"}
                </Button>
                {phaseEditMode && (
                  <Button variant="outline" onClick={addPhaseRow}>
                    Add Phase
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {phaseEditMode ? (
                <>
                  {phasesGroupedByStartDate.map(({ date, rows }) => (
                    <div key={date || "empty"} className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Program start:</span>
                        {editingProgramStartFor === date ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="date"
                              value={programStartEditValue}
                              onChange={(e) => setProgramStartEditValue(e.target.value)}
                              className="w-auto"
                            />
                            <Button
                              size="sm"
                              onClick={() => saveProgramStartForGroup(date, programStartEditValue)}
                              disabled={savingPhases}
                            >
                              Save
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingProgramStartFor(null);
                                setProgramStartEditValue("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingProgramStartFor(date);
                              setProgramStartEditValue(date || "");
                            }}
                            className="rounded border border-transparent px-2 py-1 text-sm text-gray-700 hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          >
                            {date ? formatISODateLabelLong(date) : "(no date)"}
                          </button>
                        )}
                      </div>
                      <div className="overflow-x-auto border rounded-lg">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Weeks</TableHead>
                              <TableHead>Title</TableHead>
                              <TableHead>Session</TableHead>
                              <TableHead>Disabled</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rows.map((row) => (
                              <Fragment key={row.id}>
                                <TableRow>
                                  <TableCell className="align-top">
                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="text-gray-600">Week</span>
                                      <Input
                                        type="number"
                                        min={1}
                                        className="w-16"
                                        value={row.week_start}
                                        onChange={(e) =>
                                          updatePhaseRow(row.id, {
                                            week_start: Number(e.target.value || 1),
                                          })
                                        }
                                      />
                                      <span className="text-gray-600">to</span>
                                      <Input
                                        type="number"
                                        min={1}
                                        className="w-16"
                                        value={row.week_end}
                                        onChange={(e) =>
                                          updatePhaseRow(row.id, {
                                            week_end: Number(e.target.value || 1),
                                          })
                                        }
                                      />
                                    </div>
                                    {row.phase1_start_date ? (
                                      <div className="mt-1 text-xs text-gray-500">
                                        End:{" "}
                                        {isoDatePlusDays(
                                          row.phase1_start_date,
                                          (row.week_end - 1) * 7 + 6
                                        )}
                                      </div>
                                    ) : null}
                                  </TableCell>
                                  <TableCell className="align-top">
                                    <Input
                                      value={row.title}
                                      onChange={(e) =>
                                        updatePhaseRow(row.id, { title: e.target.value })
                                      }
                                      placeholder="Phase title"
                                    />
                                  </TableCell>
                                  <TableCell className="align-top">
                                    <div className="text-sm text-gray-700">
                                      {row.session_structure_name || "—"}
                                    </div>
                                  </TableCell>
                                  <TableCell className="align-top">
                                    <Checkbox
                                      checked={row.disabled}
                                      onCheckedChange={(checked) =>
                                        updatePhaseRow(row.id, { disabled: !!checked })
                                      }
                                    />
                                  </TableCell>
                                  <TableCell className="align-top">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removePhaseRow(row.id)}
                                    >
                                      Remove
                                    </Button>
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell colSpan={5} className="border-t-0 pt-0">
                                    <div className="text-xs font-medium text-gray-600 mb-1">
                                      Details
                                    </div>
                                    <Textarea
                                      value={row.details}
                                      rows={4}
                                      onChange={(e) =>
                                        updatePhaseRow(row.id, { details: e.target.value })
                                      }
                                      placeholder="Phase details"
                                      className="min-w-[320px]"
                                    />
                                  </TableCell>
                                </TableRow>
                              </Fragment>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                  <Button onClick={savePhases} disabled={savingPhases}>
                    {savingPhases ? "Saving..." : "Save Program Phases"}
                  </Button>
                </>
              ) : (
                <div className="space-y-8">
                  {phasesGroupedByOuterPhase.map(({ date, weekGroups }) => (
                    <div key={date || "empty"} className="space-y-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Program start:</span>
                        {editingProgramStartFor === date ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="date"
                              value={programStartEditValue}
                              onChange={(e) => setProgramStartEditValue(e.target.value)}
                              className="w-auto h-7 text-xs"
                            />
                            <Button
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => saveProgramStartForGroup(date, programStartEditValue)}
                              disabled={savingPhases}
                            >
                              Save
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => {
                                setEditingProgramStartFor(null);
                                setProgramStartEditValue("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingProgramStartFor(date);
                              setProgramStartEditValue(date || "");
                            }}
                            className="text-xs text-gray-600 rounded border border-transparent px-1.5 py-0.5 hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          >
                            {date ? formatISODateLabelLong(date) : "(no date)"}
                          </button>
                        )}
                      </div>

                      {weekGroups.map((weekGroup) => (
                        <div
                          key={`${date}|${weekGroup.week_start}|${weekGroup.week_end}`}
                          className="space-y-4 rounded-xl border border-gray-200 bg-gray-50/40 p-4"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <div className="text-base font-semibold text-gray-900">
                                Weeks {weekGroup.week_start}–{weekGroup.week_end}
                              </div>
                              <div className="mt-1 flex flex-wrap gap-2">
                                {weekGroup.rows.map((row) => (
                                  <div
                                    key={row.id}
                                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm"
                                  >
                                    <span className="font-medium text-gray-900">{row.title}</span>
                                    {row.session_structure_name ? (
                                      <>
                                        <span className="mx-2 text-gray-400">•</span>
                                        <span className="text-gray-600">
                                          {row.session_structure_name}
                                        </span>
                                      </>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                if (weekGroup.anchorId) {
                                  onNavigateToWorkoutGenerator(weekGroup.anchorId);
                                }
                              }}
                              disabled={!weekGroup.anchorId || weekGroup.hasUnsaved}
                            >
                              Generate workouts
                            </Button>
                          </div>

                          {weekGroup.rows.map((row) => {
                            const detailsBlocks = parsePhaseDetailsBlocks(row.details);
                            return (
                              <div key={row.id} className="space-y-2 rounded-lg border border-gray-200 bg-white p-3">
                                <div className="font-medium text-gray-900">
                                  {row.title}
                                  {row.session_structure_name ? (
                                    <span className="ml-2 font-normal text-gray-600">
                                      · {row.session_structure_name}
                                    </span>
                                  ) : null}
                                </div>
                                {detailsBlocks.length > 0 ? (
                                  <div className="space-y-2">
                                    {detailsBlocks.map((block, idx) =>
                                      block.type === "text" ? (
                                        <div
                                          key={idx}
                                          className="whitespace-pre-wrap text-gray-700 text-xs"
                                        >
                                          {block.content}
                                        </div>
                                      ) : (
                                        <div
                                          key={idx}
                                          className="overflow-x-auto rounded-lg border border-gray-200 bg-white"
                                        >
                                          <table className="w-full min-w-[320px] text-left text-sm">
                                            <thead>
                                              <tr className="border-b border-gray-200 bg-gray-50">
                                                <th className="px-3 py-2 font-semibold text-gray-700 w-8">
                                                  #
                                                </th>
                                                {PHASE_DETAILS_COLUMNS.map((col) => (
                                                  <th
                                                    key={col}
                                                    className="px-3 py-2 font-semibold text-gray-700"
                                                  >
                                                    {col}
                                                  </th>
                                                ))}
                                              </tr>
                                            </thead>
                                            <tbody className="text-gray-900">
                                              {block.rows.map((r, rIdx) => (
                                                <tr
                                                  key={rIdx}
                                                  className="border-b border-gray-100 last:border-0"
                                                >
                                                  <td className="px-3 py-2 text-gray-500">
                                                    {r.num}.
                                                  </td>
                                                  <td className="px-3 py-2 font-medium">
                                                    {r.col1}
                                                  </td>
                                                  <td className="px-3 py-2">{r.col2}</td>
                                                  <td className="px-3 py-2 text-gray-600">
                                                    {r.col3}
                                                  </td>
                                                  <td className="px-3 py-2 text-gray-600">
                                                    {r.col4}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )
                                    )}
                                  </div>
                                ) : (
                                  <div className="whitespace-pre-wrap text-gray-600 text-xs rounded border border-gray-200 bg-gray-50 p-2">
                                    {row.details || "(no details)"}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      )}
    </Tabs>
  );
}

